import type { Middleware, UnknownAction } from '@reduxjs/toolkit';
import { TagTypes } from '../../Api/Types/tagTypes';
import { reservationsApi } from '../../Features/Reservation';
import { bookingsApi } from '../../Features/Bookings';

type ActionWithEndpointMeta = UnknownAction & {
  meta?: { arg?: { endpointName?: string } };
};

// `deleteBookingReservation` is injected onto `bookingsApi` by
// `Features/Bookings/Service/bookingReservationsApi.ts` (outside this slice's write set), so its
// static type isn't visible here. Match on the action shape RTK Query itself produces
// (`{reducerPath}/executeMutation/fulfilled` + `meta.arg.endpointName`) instead of the typed
// `matchFulfilled` helper — equivalent at runtime, and avoids depending on that module's import
// order for the endpoint to already be registered.
const isDeleteBookingReservationFulfilled = (action: UnknownAction): boolean =>
  action.type === `${bookingsApi.reducerPath}/executeMutation/fulfilled` &&
  (action as ActionWithEndpointMeta).meta?.arg?.endpointName === 'deleteBookingReservation';

/**
 * Composition-root cache bridge for reservation cancellation (DELETE /reservations/{id}).
 *
 * The same backend endpoint is called from two independent RTK Query API slices —
 * `reservationsApi` (standalone Reservation list/detail) and `bookingsApi` (via the injected
 * `deleteBookingReservation` endpoint, used by the Booking's reservation table) — each with its
 * own cache. Neither slice's own `invalidatesTags` reaches the other's cache, so cancelling a
 * reservation from one entry point would leave the other entry point showing stale (still-active)
 * data. This middleware observes both mutations fulfilling and invalidates both APIs' list reads.
 *
 * Whichever of the two mutations actually fired already self-invalidates its own list tag via its
 * own `invalidatesTags` (see `genericApiFactory.ts`'s `delete` endpoint and
 * `bookingReservationsApi.contract.ts`'s `getDeleteBookingReservationInvalidatesTags`); this
 * middleware always dispatches both invalidations regardless of which mutation matched, so one of
 * the two calls per event is a deliberate, harmless duplicate of what RTK Query already did — kept
 * uniform rather than branched, since re-invalidating an already-fresh cache is a no-op.
 *
 * Invalidation uses the bare tag type (not `{type, id: TagTypes.List}`) because the fulfilled
 * action carries no `reservationId`/`bookingId` to target a specific detail cache entry — a bare
 * type matches every tag of that type, list and detail alike, so an already-open detail page
 * (e.g. `UpdateReservation.tsx` via `useGetReservationWithAssociationsByIdQuery`, tagged
 * `{type: Reservation, id: <reservationId>}`) also refetches, not just list views.
 */
export const reservationCancellationCacheListener: Middleware = (storeApi) => (next) => (action) => {
  const result = next(action);

  const typedAction = action as UnknownAction;
  const isReservationCancellation =
    reservationsApi.endpoints.delete.matchFulfilled(typedAction) ||
    isDeleteBookingReservationFulfilled(typedAction);

  if (isReservationCancellation) {
    storeApi.dispatch(reservationsApi.util.invalidateTags([TagTypes.Reservation]));
    storeApi.dispatch(bookingsApi.util.invalidateTags([TagTypes.Booking]));
  }

  return result;
};
